import React, { useState, useLayoutEffect } from 'react';

import useInView from 'react-cool-inview';

import background from '@assets/images/virus-bg.webp';
import nature from '@assets/images/icons/nature.svg';
import world from '@assets/images/icons/world.svg';

type BlockInfoTypes = {
  headerLeft?: string;
  headerRight?: string;
  index?: number;
  isDesktopOrLaptop?: any;
  active?: boolean;
  onClick?: () => void;
};

export const BlockInfo: React.FC<BlockInfoTypes> = ({ children, ...props }) => {
  const {
    headerLeft,
    headerRight,
    index,
    active,
    onClick,
    isDesktopOrLaptop
  } = props;

  const [scrollDir, setScrollDir] = useState('down');

  useLayoutEffect(() => {
    const threshold = 0;
    let lastScrollY = window.pageYOffset;
    let ticking = false;
  
    const updateScrollDir = () => {
      const scrollY = window.pageYOffset;
  
      if (Math.abs(scrollY - lastScrollY) <= threshold) {
        ticking = false;
        return;
      }
      setScrollDir(scrollY > lastScrollY ? 'down' : 'up');
      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const updatePosition = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    }
    window.addEventListener('scroll', updatePosition);
    updatePosition();
    return () => window.removeEventListener('scroll', updatePosition);
  }, []);

  const {
    observe
  } = useInView<HTMLDivElement>({
    threshold: 0.50, // Default is 0
    onChange: ({ observe, unobserve }) => {
      // Triggered whenever the target meets a threshold, e.g. [0.25, 0.5, ...]
      unobserve(); // To stop observing the current target element
      observe(); // To re-start observing the current target element
    },
    onEnter: ({ entry }) => {
      // Triggered when the target enters the viewport
      const { target } = entry;

      if (scrollDir === 'down') {
        target.classList.add('on');
      }

    },
    onLeave: ({ entry }) => {
      const { target } = entry;

      if (scrollDir === 'up') {
        target.classList.remove('on');
      }

    },
  });

  return (
    <div
      ref={observe}
      id={index === 0 ? 'first': `item-${index}`}
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: `${isDesktopOrLaptop ? 'repeat-y' : 'no-repeat' }`,
        backgroundPosition: '50%'
      }}
      className="
        portrait:bg-auto
        landscape:bg-cover
        lg:bg-full
        relative
        z-10
        max-w-1160
        mx-5
        lg:mx-auto
        flex
        flex-col
        lg:flex-row
        justify-between
        lg:w-full
        text-white
        rounded-2.5xl
        pt-2.5
        lg:pt-9
        pb-18
        lg:pb-18
        mb-10
        lg:mb-0
        in-view

      "
    >
      {(!isDesktopOrLaptop && !active || isDesktopOrLaptop) && <div
        className="flex-1 lg:px-2"
      >
        <div className="pl-5 lg:pl-15 pr-5 lg:pr-0 flex-1">
          <span className="in-view-dots inline-flex justify-center items-center rounded-full bg-white w-15 h-15 mb-8">
            <img src={world} width="34" height="44" alt="" />
          </span>
          <div className="in-view-text pl-5 py-0 text-2xl lg:text-3xl border-l leading-8.5 lg:leading-11">
            {headerLeft}
          </div>
        </div>
      </div>}
      {(!isDesktopOrLaptop && active || isDesktopOrLaptop) && <div
        className="flex-1 lg:px-2"
      >
        <div className="pl-5 lg:pl-0 pr-5 lg:pr-15 flex-1">
          <span className="in-view-dots inline-flex justify-center items-center rounded-full bg-white w-15 h-15 mb-8">
            <img src={nature} width="23" height="36" alt="" />
          </span>
          <div className="in-view-text pl-5 py-0 text-2xl lg:text-3xl border-l leading-8.5 lg:leading-11">
            {headerRight}
          </div>
        </div>
      </div>}
    </div>
  );
}